import { useLazyQuery } from "@apollo/client/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useTypedParams, useTypedSearchParams } from "react-router-typesafe-routes";
import { toast } from "sonner";

import { DetailLoading } from "~/components/loading";
import { EditMapToolsProvider } from "~/features/recordGeoItems/contexts/EditMapToolsContext";
import {
  GetAreaGeoItemDetailDocument,
  type GetAreaGeoItemDetailQuery,
  type GetAreaGeoItemDetailQueryVariables,
} from "~/features/recordGeoItems/gqls";
import { useAreaInputContext, useRecordGeoItemCreate } from "~/features/recordGeoItems/hooks";
import { emptyRecordGeoItemForm, type RecordGeoItemFormData } from "~/features/recordGeoItems/types";
import { parseGeometry } from "~/features/recordGeoItems/utils/parseGeometry";
import { parseH3IndexesStr } from "~/features/recordGeoItems/utils/parseH3IndexStr";
import { useTenant } from "~/features/tenant/context";
import { v2Route } from "~/v2/routes";
import { RecordGeoItemForm } from "./RecordGeoItemForm";

type AreaDetail = Extract<
  GetAreaGeoItemDetailQuery["cnutGetRecordGeoItemDetail"],
  { __typename: "RecordGeoItemDetailOutput" }
>;

/** Map an existing area's detail onto create-form defaults (used when duplicating an area). */
function toFormDefaults(rgi: AreaDetail): RecordGeoItemFormData {
  // The form writes one description; a legacy-only pair has exactly one entry, its join row's.
  const entry = rgi.entries[0];
  const i18n = (entry?.i18nLabel ?? {}) as { ja?: string; en?: string };
  const metadata = (entry?.metadata ?? {}) as { memo?: string };
  return {
    date: entry?.date ?? "",
    endDate: entry?.endDate ?? "",
    jaLabel: i18n.ja ?? "",
    enLabel: i18n.en ?? "",
    h3Indexes: parseH3IndexesStr(rgi.geoItem.h3Indexes ?? null),
    geometry: parseGeometry(rgi.geoItem.geometry ?? null),
    memo: metadata.memo ?? "",
    evidences: rgi.evidences.map((evidence) => {
      const ref = evidence.references[0];
      return {
        reference: ref ? { id: ref.id, title: ref.title } : null,
        citation: { page: (evidence.citation as { page?: string } | null)?.page },
      };
    }),
  };
}

/** Create a new geo-item area for a record. Mounted at `/area-input/:recordId/create`. */
export function RecordGeoItemCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { recordId = "" } = useTypedParams(v2Route.areaInput.record);
  const { tenantId } = useTenant();
  const [{ duplicateGeoItemId, h3IndexesFileURLs }] = useTypedSearchParams(v2Route.areaInput.record.create);
  const { data } = useAreaInputContext();

  const { submit, processing } = useRecordGeoItemCreate({
    recordId,
    onCreated: (geoItemId) => navigate(v2Route.areaInput.record.detail.$buildPath({ params: { recordId, geoItemId } })),
  });

  // Seed sources (mutually exclusive): duplicate an existing area, or H3-index files from the dialog.
  const fileUrls = useMemo(() => h3IndexesFileURLs ?? [], [h3IndexesFileURLs]);

  const [defaultValues, setDefaultValues] = useState<RecordGeoItemFormData | null>(
    fileUrls.length === 0 && !duplicateGeoItemId ? emptyRecordGeoItemForm : null,
  );

  const [getArea] = useLazyQuery<GetAreaGeoItemDetailQuery, GetAreaGeoItemDetailQueryVariables>(
    GetAreaGeoItemDetailDocument,
  );

  // Prefill from the area being duplicated.
  useEffect(() => {
    if (!duplicateGeoItemId || !tenantId) return;
    let cancelled = false;
    getArea({ variables: { tenantId, recordId, geoItemId: duplicateGeoItemId } })
      .then((res) => {
        if (cancelled) return;
        const response = res.data?.cnutGetRecordGeoItemDetail;
        const rgi = response?.__typename === "RecordGeoItemDetailOutput" ? response : null;
        setDefaultValues(rgi ? toFormDefaults(rgi) : emptyRecordGeoItemForm);
      })
      .catch(() => {
        if (cancelled) return;
        setDefaultValues(emptyRecordGeoItemForm);
      });
    return () => {
      cancelled = true;
    };
  }, [duplicateGeoItemId, tenantId, recordId, getArea]);

  // Prefill from uploaded H3-index files.
  useEffect(() => {
    if (duplicateGeoItemId) return;
    if (fileUrls.length === 0) {
      setDefaultValues(emptyRecordGeoItemForm);
      return;
    }
    let cancelled = false;
    Promise.all(fileUrls.map((url) => fetch(url).then((res) => res.json())))
      .then((arrays: unknown[][]) => {
        if (cancelled) return;
        const h3Indexes = Array.from(new Set(arrays.flat().map((v) => (typeof v === "string" ? v : String(v)))));
        setDefaultValues({ ...emptyRecordGeoItemForm, h3Indexes });
      })
      .catch(() => {
        if (cancelled) return;
        toast.error(t("feature.recordGeoItems.createWithH3IndexesFileDialog.alert.failedToLoad"));
        setDefaultValues(emptyRecordGeoItemForm);
      });
    return () => {
      cancelled = true;
    };
  }, [fileUrls, duplicateGeoItemId, t]);

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <h1 className="text-dewey-title capitalize text-fg-1">{t("feature.geoItems.create.title")}</h1>
      {defaultValues === null ? (
        <DetailLoading />
      ) : (
        <EditMapToolsProvider>
          <RecordGeoItemForm
            references={data.references}
            defaultValues={defaultValues}
            processing={processing}
            submitLabel={t("ui.create")}
            onSubmit={(form) => void submit(form)}
            onCancel={() => navigate(-1)}
          />
        </EditMapToolsProvider>
      )}
    </div>
  );
}
